import * as React from "react";
import CodeMirror, {
  EditorView,
  type ReactCodeMirrorProps,
} from "@uiw/react-codemirror";
import { html } from "@codemirror/lang-html";
import { oneDark } from "@codemirror/theme-one-dark";
import { cn } from "../../../lib/utils";
import { Button } from "../button";

export interface HtmlEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  className?: string;
  readOnly?: boolean;
  /**
   * When true, pretty-prints the value with Prettier the first time a
   * non-empty value arrives. Useful for minified or collapsed HTML loaded
   * from the API. Defaults to true.
   */
  autoFormatOnLoad?: boolean;
}

const BASE_SETUP: ReactCodeMirrorProps["basicSetup"] = {
  lineNumbers: true,
  foldGutter: true,
  bracketMatching: true,
  highlightActiveLine: true,
  highlightActiveLineGutter: true,
  autocompletion: true,
  closeBrackets: true,
  indentOnInput: true,
  tabSize: 2,
};

/* CodeMirror takes a style object, not classes, so the type tokens are read as
   CSS vars rather than via `text-sm` / `font-mono`. Tailwind only emits a
   default-theme var once some utility uses it, so each carries the opero-app
   value as a fallback. */
const CODE_FONT_THEME = EditorView.theme({
  "&": {
    fontSize: "var(--text-sm, 0.8125rem)",
    lineHeight: "var(--text-sm--line-height, 1.125rem)",
  },
  ".cm-content": {
    fontFamily:
      "var(--font-mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace)",
    padding: "12px 0",
  },
  ".cm-gutters": {
    backgroundColor: "transparent",
  },
});

async function formatHtml(source: string): Promise<string> {
  if (!source.trim()) return source;
  const [prettier, pluginHtml] = await Promise.all([
    import("prettier/standalone"),
    import("prettier/plugins/html"),
  ]);
  return prettier.format(source, {
    parser: "html",
    plugins: [pluginHtml.default ?? pluginHtml],
    printWidth: 100,
    tabWidth: 2,
    htmlWhitespaceSensitivity: "ignore",
    bracketSameLine: true,
  });
}

function useDarkMode(): boolean {
  const [isDark, setIsDark] = React.useState(() => {
    if (typeof document === "undefined") return false;
    return document.documentElement.classList.contains("dark");
  });
  React.useEffect(() => {
    if (typeof MutationObserver === "undefined") return;
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

export const HtmlEditor = React.forwardRef<HTMLDivElement, HtmlEditorProps>(
  (
    {
      value,
      onChange,
      placeholder,
      height = "100%",
      minHeight,
      maxHeight,
      className,
      readOnly,
      autoFormatOnLoad = true,
    },
    ref,
  ) => {
    const isDark = useDarkMode();
    const valueRef = React.useRef(value);
    valueRef.current = value;
    const autoFormattedKey = React.useRef<string | null>(null);
    const [formatting, setFormatting] = React.useState(false);
    const [formatError, setFormatError] = React.useState<string | null>(null);

    const extensions = React.useMemo(
      () => [
        html({ matchClosingTags: true, autoCloseTags: true }),
        EditorView.lineWrapping,
        CODE_FONT_THEME,
      ],
      [],
    );

    const runFormat = React.useCallback(async () => {
      const source = valueRef.current;
      setFormatting(true);
      setFormatError(null);
      try {
        const next = await formatHtml(source);
        if (valueRef.current !== source) return;
        if (next !== source) onChange(next);
      } catch (err) {
        setFormatError(
          err instanceof Error ? err.message : "Could not format HTML",
        );
      } finally {
        setFormatting(false);
      }
    }, [onChange]);

    React.useEffect(() => {
      if (!autoFormatOnLoad) return;
      if (!value.trim()) return;
      if (autoFormattedKey.current === value) return;
      autoFormattedKey.current = value;
      void runFormat();
    }, [value, autoFormatOnLoad, runFormat]);

    return (
      <div
        ref={ref}
        className={cn(
          // Raised tier, not a boxed edge (`docs/borderless-contract.md` §1/§2).
          // `shadow-sm` was already a no-op — the token scale pins it to `none`
          // — so the hairline was carrying the whole shape; `shadow-raised`
          // replaces it. `bg-panel` is the tokenised form of the old
          // `bg-white dark:bg-slate-950` pair.
          "flex h-full min-h-0 flex-col overflow-hidden rounded-md bg-panel text-sm shadow-raised",
          className,
        )}
      >
        {/* The toolbar strip is a `bg-sunken` well rather than a bottom rule: it
            cannot be `bg-panel` (that would nest a raised surface in a raised
            surface, §2) and the old `bg-slate-50` is indistinguishable from the
            panel once the hairline is gone. */}
        <div className="flex items-center justify-between gap-2 bg-sunken px-2 py-1.5 text-xs">
          <span className="pl-1 font-medium uppercase tracking-wide text-slate-500">
            HTML
          </span>
          <div className="flex items-center gap-2">
            {formatError && (
              <span className="max-w-60 truncate text-red-500">
                {formatError}
              </span>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={runFormat}
              disabled={formatting || readOnly}
              className="h-7 px-2 text-xs"
            >
              {formatting ? "Formatting…" : "Format"}
            </Button>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden [&_.cm-editor]:h-full [&_.cm-scroller]:overflow-auto">
          <CodeMirror
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            readOnly={readOnly}
            basicSetup={BASE_SETUP}
            extensions={extensions}
            height={height}
            minHeight={minHeight}
            maxHeight={maxHeight}
            theme={isDark ? oneDark : "light"}
            className="h-full"
          />
        </div>
      </div>
    );
  },
);

HtmlEditor.displayName = "HtmlEditor";
